import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { submitInquiry } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const fieldClass =
  "mt-2 h-12 rounded-lg border-[var(--mx-line-strong)] bg-[var(--mx-surface)] text-[var(--mx-ink)] placeholder:text-[var(--mx-ink-3)] transition-colors duration-300 focus-visible:border-[var(--mx-copper)] focus-visible:ring-2 focus-visible:ring-[var(--mx-copper)]/20";

const commodities = ["Refined Lead", "Lead Alloys", "Other"];
const intents = [
  { value: "buy", label: "Buy refined lead / alloys" },
  { value: "sell", label: "Sell scrap / batteries" },
  { value: "recycle", label: "Recycling enquiry" },
  { value: "partnership", label: "Partnership" },
];

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  commodity: "",
  intent: "",
  volume: "",
  message: "",
};

const labelClass =
  "font-mono-tech text-xs uppercase tracking-widest text-[var(--mx-ink-2)]";

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.commodity || !form.intent) {
      toast.error("Please complete name, email, commodity and intent.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry(form);
      toast.success("Enquiry received — our desk will respond within 24h.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="border-t border-[var(--mx-line)] bg-[var(--mx-surface-2)]"
    >
      <div className="mx-auto grid max-w-[1400px] gap-16 px-6 py-28 md:grid-cols-12 md:px-10 md:py-40">
        <div className="md:col-span-5">
          <Reveal>
            <div className="mb-8 flex items-center gap-4 font-mono-tech text-xs uppercase tracking-[0.25em] text-[var(--mx-ink-2)]">
              <span className="h-px w-10 bg-[var(--mx-copper)]" />
              07 — Enquire
            </div>
            <h2 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter text-[var(--mx-ink)] md:text-7xl">
              Request a<br />
              <span className="text-metal">quote</span>
            </h2>
            <p className="mt-8 max-w-sm text-base leading-relaxed text-[var(--mx-ink-2)]">
              Tell us what you need — refined lead or custom lead alloys — and
              our team will respond with pricing and lead times within one
              business day.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="grid gap-6 rounded-2xl border border-[var(--mx-line)] bg-[var(--mx-surface)] p-6 shadow-[0_24px_70px_-50px_rgba(27,23,20,0.5)] sm:grid-cols-2 md:p-8"
            >
              <div>
                <Label className={labelClass}>Full name *</Label>
                <Input
                  data-testid="quote-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Doe"
                  className={fieldClass}
                />
              </div>
              <div>
                <Label className={labelClass}>Company</Label>
                <Input
                  data-testid="quote-company"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Acme Metals Ltd"
                  className={fieldClass}
                />
              </div>
              <div>
                <Label className={labelClass}>Email *</Label>
                <Input
                  data-testid="quote-email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="jane@company.com"
                  className={fieldClass}
                />
              </div>
              <div>
                <Label className={labelClass}>Phone</Label>
                <Input
                  data-testid="quote-phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+91 ..."
                  className={fieldClass}
                />
              </div>
              <div>
                <Label className={labelClass}>Product *</Label>
                <Select value={form.commodity} onValueChange={set("commodity")}>
                  <SelectTrigger
                    data-testid="quote-commodity"
                    className={`${fieldClass} w-full`}
                  >
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-[var(--mx-line-strong)] bg-[var(--mx-surface)] text-[var(--mx-ink)]">
                    {commodities.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelClass}>Intent *</Label>
                <Select value={form.intent} onValueChange={set("intent")}>
                  <SelectTrigger
                    data-testid="quote-intent"
                    className={`${fieldClass} w-full`}
                  >
                    <SelectValue placeholder="Select intent" />
                  </SelectTrigger>
                  <SelectContent className="rounded-lg border-[var(--mx-line-strong)] bg-[var(--mx-surface)] text-[var(--mx-ink)]">
                    {intents.map((i) => (
                      <SelectItem key={i.value} value={i.value}>
                        {i.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className={labelClass}>Estimated volume</Label>
                <Input
                  data-testid="quote-volume"
                  value={form.volume}
                  onChange={set("volume")}
                  placeholder="e.g. 25 MT / month"
                  className={fieldClass}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className={labelClass}>Message</Label>
                <Textarea
                  data-testid="quote-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Specifications, grade, delivery terms..."
                  className={`${fieldClass} h-auto`}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit"
                className="group relative col-span-full overflow-hidden rounded-full bg-[var(--mx-copper)] px-8 py-4 font-mono-tech text-xs uppercase tracking-widest text-white transition-opacity duration-300 disabled:opacity-60"
              >
                <span className="relative z-10">
                  {loading ? "Submitting…" : "Submit enquiry →"}
                </span>
                <span className="absolute inset-0 -translate-x-full bg-[var(--mx-charcoal)] transition-transform duration-300 group-hover:translate-x-0" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
